import React, { useState, useEffect } from 'react';
import {
  useNotify,
  useDataProvider,
  useRefresh,
} from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import BuildIcon from '@mui/icons-material/Build';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import SpeedIcon from '@mui/icons-material/Speed';
import PersonIcon from '@mui/icons-material/Person';

interface MaintenanceShowDialogProps {
  open: boolean;
  onClose: () => void;
  recordId: number | null;
  onEdit?: () => void;
}


export const MaintenanceShowDialog: React.FC<MaintenanceShowDialogProps> = ({ 
  open, 
  onClose, 
  recordId,
  onEdit 
}) => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const [loading, setLoading] = useState(false);
  const [record, setRecord] = useState<any>(null);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  useEffect(() => {
    if (open && recordId) {
      setLoading(true);
      dataProvider.getOne('equipment-maintenance', { id: recordId })
        .then(({ data }) => {
          setRecord(data);
        })
        .catch((error) => {
          console.error('Error loading maintenance record:', error);
          notify('Error loading maintenance record', { type: 'error' });
          onClose();
        })
        .finally(() => {
          setLoading(false);
        });
    } else if (!open) {
      setRecord(null);
    }
  }, [open, recordId, dataProvider, notify, onClose]);

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount?: number) => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatNumber = (value?: number) => {
    if (value === undefined || value === null) return '-';
    return value.toLocaleString();
  };

  const handleDownloadFile = (file: any) => {
    if (file.file?.url) {
      window.open(file.file.url, '_blank');
    }
  };

  const handleViewImage = (file: any) => {
    if (file.file?.url && file.file.mimeType?.startsWith('image/')) {
      setSelectedImage(file.file.url);
      setImageDialogOpen(true);
    } else {
      handleDownloadFile(file);
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown size';
    const mb = bytes / (1024 * 1024);
    return mb < 1 ? `${(bytes / 1024).toFixed(1)} KB` : `${mb.toFixed(1)} MB`;
  };

  const getServiceTypeColor = (type: string) => {
    switch (type) {
      case 'PREVENTIVE': return 'success';
      case 'EMERGENCY': return 'error';
      case 'REPAIR': return 'warning';
      case 'INSPECTION': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  if (!record) return null;

  const files = record.files || [];

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h6">Maintenance Record Details</Typography>
            <Chip 
              label={record.serviceType?.replace(/_/g, ' ')} 
              color={getServiceTypeColor(record.serviceType)}
              size="small"
            />
          </Box>
          <Box>
            {onEdit && (
              <IconButton onClick={onEdit} size="small" sx={{ mr: 1 }}>
                <EditIcon />
              </IconButton>
            )}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers sx={{ p: 0 }}>
          {/* Service Information */}
          <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Service Information
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CalendarTodayIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Service Date</Typography>
                    <Typography variant="body1">{formatDate(record.serviceDate)}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <BuildIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Service Type</Typography>
                    <Typography variant="body1">{record.serviceType?.replace(/_/g, ' ')}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <SpeedIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Service Mileage</Typography>
                    <Typography variant="body1">{formatNumber(record.serviceMileage)}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <PersonIcon color="action" fontSize="small" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">Service Provider</Typography>
                    <Typography variant="body1">
                      {record.serviceProvider?.name || 'No Provider'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Cost Information */}
          {record.totalCost !== undefined && record.totalCost !== null && (
            <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Cost Information
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AttachMoneyIcon color="action" fontSize="small" />
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Cost</Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary">
                    {formatCurrency(record.totalCost)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {/* Description */}
          {record.description && (
            <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Service Description
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {record.description}
              </Typography>
            </Box>
          )}

          {/* Notes */}
          {record.notes && (
            <Box sx={{ p: 3, borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Additional Notes
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {record.notes}
              </Typography>
            </Box>
          )}

          {/* File Attachments */}
          <Box sx={{ p: 3 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              File Attachments ({files.length})
            </Typography>
            
            {files.length === 0 ? (
              <Alert severity="info" variant="outlined" sx={{ mt: 2 }}>
                No files attached to this maintenance record.
              </Alert>
            ) : (
              <List sx={{ mt: 2 }}>
                {files.map((file: any, index: number) => (
                  <ListItem
                    key={file.id || index}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      '&:last-child': { mb: 0 },
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemIcon>
                      <AttachFileIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={file.file?.name || 'Unnamed file'}
                      secondary={formatFileSize(file.file?.size)}
                    />
                    <ListItemSecondaryAction>
                      {file.file?.mimeType?.startsWith('image/') && (
                        <IconButton
                          edge="end"
                          aria-label="view"
                          onClick={() => handleViewImage(file)}
                          sx={{ mr: 1 }}
                        >
                          <OpenInNewIcon />
                        </IconButton>
                      )}
                      <IconButton
                        edge="end"
                        aria-label="download"
                        onClick={() => handleDownloadFile(file)}
                        disabled={!file.file?.url}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Image Viewer Dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">View Image</Typography>
          <IconButton onClick={() => setImageDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          {selectedImage && (
            <img 
              src={selectedImage} 
              alt="Maintenance attachment" 
              style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};